import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, Check, MapPin, Smartphone } from 'react-feather';

const MAX_PHOTO_WIDTH = 720;

function stopStream(stream) {
	if (stream) {
		stream.getTracks().forEach((track) => track.stop());
	}
}

function StatusLine({ icon: Icon, label, status }) {
	const tone =
		status === 'ready'
			? 'text-green-600'
			: status === 'error'
				? 'text-red-600'
				: 'text-gray-500';

	const text =
		status === 'ready'
			? 'Ready'
			: status === 'error'
				? 'Unavailable'
				: 'Checking...';

	return (
		<div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
			<div className="flex items-center gap-2 text-sm text-gray-700">
				<Icon size={16} />
				{label}
			</div>
			<span className={`text-sm font-medium ${tone}`}>{text}</span>
		</div>
	);
}

export default function ClockCaptureModal({
	isOpen,
	actionLabel,
	onCapture,
	onClose,
	isSaving,
}) {
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const coordsRef = useRef(null);
	const [cameraStatus, setCameraStatus] = useState('idle'); // idle | requesting | ready | error
	const [geoStatus, setGeoStatus] = useState('idle');

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		let isCancelled = false;

		// --- Camera ---
		setCameraStatus('requesting');
		if (!navigator.mediaDevices?.getUserMedia) {
			setCameraStatus('error');
		} else {
			navigator.mediaDevices
				.getUserMedia({ video: { facingMode: 'user' }, audio: false })
				.then((stream) => {
					if (isCancelled) {
						stopStream(stream);
						return;
					}

					streamRef.current = stream;
					if (videoRef.current) {
						videoRef.current.srcObject = stream;
						videoRef.current.play?.().catch(() => {});
					}
					setCameraStatus('ready');
				})
				.catch(() => {
					if (!isCancelled) {
						setCameraStatus('error');
					}
				});
		}

		// --- Geolocation ---
		setGeoStatus('requesting');
		if (!navigator.geolocation) {
			setGeoStatus('error');
		} else {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					if (isCancelled) {
						return;
					}

					coordsRef.current = {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					};
					setGeoStatus('ready');
				},
				() => {
					if (!isCancelled) {
						setGeoStatus('error');
					}
				},
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
			);
		}

		return () => {
			isCancelled = true;
			stopStream(streamRef.current);
			streamRef.current = null;
			coordsRef.current = null;
			setCameraStatus('idle');
			setGeoStatus('idle');
		};
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	const isReady = cameraStatus === 'ready' && geoStatus === 'ready';
	const isBlocked = cameraStatus === 'error' || geoStatus === 'error';

	const handleCapture = () => {
		const video = videoRef.current;
		const coords = coordsRef.current;

		if (!video || !coords) {
			return;
		}

		const sourceWidth = video.videoWidth || 640;
		const sourceHeight = video.videoHeight || 480;
		const scale = Math.min(1, MAX_PHOTO_WIDTH / sourceWidth);

		const canvas = document.createElement('canvas');
		canvas.width = Math.round(sourceWidth * scale);
		canvas.height = Math.round(sourceHeight * scale);

		const context = canvas.getContext('2d');
		if (!context) {
			return;
		}

		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		canvas.toBlob(
			(blob) => {
				if (!blob) {
					return;
				}

				const photoFile = new File([blob], 'attendance-photo.jpg', {
					type: 'image/jpeg',
				});

				onCapture({
					photoFile,
					latitude: coords.latitude,
					longitude: coords.longitude,
				});
			},
			'image/jpeg',
			0.7
		);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
			<div className="w-full max-w-lg rounded-md bg-white p-6 shadow-xl">
				<div className="mb-4 flex items-start justify-between gap-4">
					<div>
						<h3 className="text-xl font-semibold text-gray-900">{actionLabel}</h3>
						<p className="mt-1 text-sm text-gray-500">
							Take a photo and confirm your location to continue.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Cancel
					</button>
				</div>

				<div className="relative aspect-video w-full overflow-hidden rounded-md border border-gray-200 bg-gray-900">
					<video
						ref={videoRef}
						className="h-full w-full object-cover"
						autoPlay
						playsInline
						muted
					/>
					{cameraStatus !== 'ready' ? (
						<div className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-gray-200">
							{cameraStatus === 'error'
								? 'Camera unavailable'
								: 'Starting camera...'}
						</div>
					) : null}
				</div>

				<div className="mt-4 grid gap-2 sm:grid-cols-2">
					<StatusLine icon={Camera} label="Camera" status={cameraStatus} />
					<StatusLine icon={MapPin} label="Location" status={geoStatus} />
				</div>

				{isBlocked ? (
					<div className="mt-4 flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
						<AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
						<div className="text-sm text-amber-800">
							<p className="font-semibold">Can&apos;t clock in / out from here</p>
							<p className="mt-1">
								This device doesn&apos;t have a camera and location enabled, which are
								required to record your time. Please use the{' '}
								<span className="inline-flex items-center gap-1 font-semibold">
									<Smartphone size={14} /> mobile app
								</span>{' '}
								to clock in or out instead.
							</p>
						</div>
					</div>
				) : null}

				<div className="mt-5 flex flex-wrap justify-end gap-3">
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Close
					</button>
					<button
						type="button"
						onClick={handleCapture}
						disabled={!isReady || isSaving}
						className="inline-flex items-center gap-2 rounded-md bg-secondColor-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-secondColor-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isSaving ? (
							'Saving...'
						) : (
							<>
								<Check size={16} />
								Capture &amp; {actionLabel}
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
